import { Button, Grid, NumberInput, Select, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import PaymentConfigEnum from '../../enum/PaymentConfig';
import { useGetCashierboxes } from '../../hooks/cashierboxes/queries';
import { useForm } from '@mantine/form';
import { requiredFieldFormValidator } from '../../utils/validators/formValidators';
import { showNotification } from '../../utils/Notification/Notification';
import { memo, useEffect, useRef, useState } from 'react';
import { useBulkCreatePaymentLines } from '../../hooks/orders/mutations';
import { PaymentMethodsEnum } from '../../enum/PaymentMethodsEnum';
import { useSelector } from 'react-redux';
import { IUser } from '../../interfaces/User';
import { DEFAULT_FOLIO_PREFIX } from '../../constants';
import { buildOrderFolio } from '../../utils/ordersUtils';
import { useGetStoreById } from '../../hooks/stores/queries';
import { autoOpenCashDrawerForPayments } from '../../utils/cashDrawer';

export default memo(function BulkOrderAddPaymentLineModal({
  close,
  paymentLines,
  storeId,
  onPaymentLinesCreated,
  disableAmountField = false,
  beforeSubmit,
  onSubmittingChange
}: {
  close: () => void;
  storeId: string;
  paymentLines: {
    sequence: string;
    folioPrefix?: string | null;
    orderId: string;
    paymentMethod: PaymentMethodsEnum;
    amount: number;
    cashierBoxId: string;
    paymentDate: Date;
  }[];
  onPaymentLinesCreated?: (successFullOrders: string[]) => unknown | Promise<unknown>;
  disableAmountField?: boolean;
  beforeSubmit?: (paymentLines: { orderId: string; amount: number }[]) => Promise<boolean>;
  onSubmittingChange?: (submitting: boolean) => void;
}) {
  const { t } = useTranslation([
    'OrdersPage',
    'FieldName',
    'CommonButtons',
    'UpdateOrderForm',
    'Notifications'
  ]);
  const setBulkCreatePaymentLines = useBulkCreatePaymentLines();
  const submitting = useRef(false);
  const [bulkFailedRows, setBulkFailedRows] = useState([]);
  const { user } = useSelector(
    (state: { user: { user: Pick<IUser, 'cashierBox'> } }) => state.user
  );
  const { isLoading: isGetCashierboxesLoading, data: cashierboxes } = useGetCashierboxes(
    !!storeId,
    storeId
  );
  const { data: store } = useGetStoreById(!!storeId, storeId);
  const form = useForm({
    mode: 'controlled',
    initialValues: { paymentLines: paymentLines || [] },
    validate: {
      paymentLines: {
        amount: (value) => {
          const requiredValidatorRes = requiredFieldFormValidator(value);
          if (requiredValidatorRes) return requiredValidatorRes;
        },
        paymentMethod: (value) => {
          const requiredValidatorRes = requiredFieldFormValidator(value);
          if (requiredValidatorRes) return requiredValidatorRes;
        },
        cashierBoxId: (value) => {
          const requiredValidatorRes = requiredFieldFormValidator(value);
          if (requiredValidatorRes) return requiredValidatorRes;
        }
      }
    }
  });

  const handleSubmit = async ({
    paymentLines
  }: {
    paymentLines: {
      sequence: string;
      folioPrefix?: string | null;
      orderId: string;
      paymentMethod: PaymentMethodsEnum;
      amount: number;
      cashierBoxId: string;
      paymentDate: Date;
    }[];
  }) => {
    if (submitting.current || setBulkCreatePaymentLines.isPending) return;
    submitting.current = true;
    onSubmittingChange?.(true);
    try {
      if (beforeSubmit && !(await beforeSubmit(paymentLines))) return;
      if (
        paymentLines.some((line) => !cashierboxes?.some((box) => box._id === line.cashierBoxId))
      ) {
        showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
        return;
      }
      const res = await setBulkCreatePaymentLines.mutateAsync({
        storeId: storeId,
        body: {
          paymentLines
        }
      });
      const successfulOrderIds = new Set(
        res?.ordersSuccess?.map((row: { orderId: string }) => row.orderId) || []
      );
      const successfulPaymentLines = paymentLines
        .filter((paymentLine) => successfulOrderIds.has(paymentLine.orderId))
        .map((paymentLine) => ({
          paymentMethod: paymentLine.paymentMethod,
          amountPaid: paymentLine.amount
        }));
      void autoOpenCashDrawerForPayments({
        cashDrawerConfig: store?.cashDrawerConfig,
        payments: successfulPaymentLines,
        source: 'payment'
      }).then((cashDrawerResult) => {
        if (cashDrawerResult.attempted && !cashDrawerResult.success) {
          showNotification({
            type: 'Error',
            title: t('Notifications:cashDrawerOpenFailedTitle'),
            message: cashDrawerResult.errorMessage || t('Notifications:cashDrawerOpenFailedBody')
          });
        }
      });
      if (onPaymentLinesCreated) {
        await onPaymentLinesCreated(
          res?.ordersSuccess.map(
            (row: { sequence: number; orderId: any; error: any[] }) => row.orderId
          )
        );
      }
      //WE ALWAYS SHOW THE SUCCESS MESSAGE IN CASE WE CREATED SOME LINES
      res?.ordersSuccess?.length &&
        showNotification({
          type: 'Default',
          title: `${t('Notifications:success')}, ${res?.ordersSuccess?.length} registros creados`
        });
      if (res?.ordersFails?.length) {
        showNotification({
          type: 'Info',
          title: `${res.ordersFails.length} registro(s) no pudieron crearse`
        });
        setBulkFailedRows(
          res?.ordersFails.map(
            (row: { sequence: number; orderId: any; error: any[] }) => row?.sequence
          )
        );
        throw Error('Some Rows Failed');
      } else {
        setBulkFailedRows([]);
        close();
      }
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw e;
    } finally {
      submitting.current = false;
      onSubmittingChange?.(false);
    }
  };

  useEffect(() => {
    if (cashierboxes) {
      const initialCashierBox =
        typeof user.cashierBox === 'string' &&
        cashierboxes.some((box) => box._id === user.cashierBox)
          ? user.cashierBox
          : (cashierboxes || [])[0]?._id;
      form.setValues({
        paymentLines: paymentLines.map((paymentLine) => ({
          ...paymentLine,
          cashierBoxId: initialCashierBox
        }))
      });
      form.resetDirty({
        paymentLines: paymentLines.map((paymentLine) => ({
          ...paymentLine,
          cashierBoxId: initialCashierBox
        }))
      });
    }
  }, [cashierboxes, paymentLines]);

  if (!paymentLines) return <LoaderOverlay visible={true} />;

  return (
    <>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Grid gutter="md">
          <Grid.Col>
            <LoaderOverlay visible={isGetCashierboxesLoading} />
          </Grid.Col>
          {bulkFailedRows.length > 0 ? (
            <Grid.Col span={12}>
              <Text c="red" fw="bold">
                {t('UpdateOrderForm:errors.verifyPaymentLines')}
              </Text>
              <Text fw="bold">
                {bulkFailedRows
                  .map((orderSequence) => buildOrderFolio(DEFAULT_FOLIO_PREFIX, orderSequence))
                  .join(',')}
              </Text>
            </Grid.Col>
          ) : (
            <>
              <Grid.Col span={12}>
                <Grid>
                  <Grid.Col span={1}>{t('FieldName:order')}</Grid.Col>
                  <Grid.Col span={3}>{t('FieldName:cashierBox')}</Grid.Col>
                  <Grid.Col span={3}>{t('FieldName:paymentMethod')}</Grid.Col>
                  <Grid.Col span={3}>{t('FieldName:amount')}</Grid.Col>
                </Grid>
              </Grid.Col>
              <Grid.Col span={12}>
                {form.getValues().paymentLines?.map((paymentLine, index) => {
                  return (
                    <Grid key={`${paymentLine.orderId || paymentLine.sequence || index}`}>
                      <Grid.Col span={1}>
                        {buildOrderFolio(paymentLine?.folioPrefix, paymentLine?.sequence)}
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <Select
                          allowDeselect={false}
                          placeholder={t('FieldName:cashierBox')}
                          defaultValue={paymentLine.cashierBoxId || ''}
                          data={(cashierboxes || [])
                            .filter((box) => !!box._id)
                            .map((cashierbox) => {
                              return {
                                label: cashierbox.name,
                                value: cashierbox._id!,
                                key: cashierbox._id
                              };
                            })}
                          {...form.getInputProps(`paymentLines.${index}.cashierBoxId`)}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <Select
                          allowDeselect={false}
                          placeholder={t('FieldName:paymentMethod')}
                          data={Object.values(PaymentConfigEnum)
                            .filter((method) => method !== 'delivery')
                            .map((method) => ({
                              value: method,
                              label: t(`FieldName:paymentMethods.${method}`)
                            }))}
                          {...form.getInputProps(`paymentLines.${index}.paymentMethod`)}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <NumberInput
                          disabled={disableAmountField}
                          placeholder={t('FieldName:amount')}
                          allowLeadingZeros={false}
                          allowNegative={false}
                          {...form.getInputProps(`paymentLines.${index}.amount`)}
                        />
                      </Grid.Col>
                    </Grid>
                  );
                })}
              </Grid.Col>
              <Grid.Col>
                <Button type="submit" fullWidth loading={setBulkCreatePaymentLines.isPending}>
                  {t('CommonButtons:submit')}
                </Button>
              </Grid.Col>
            </>
          )}
        </Grid>
      </form>
    </>
  );
});
